import { AnalyticsEvent, useAnalytics } from '@aninix/analytics'
import * as React from 'react'

export interface IProps {}
export const PromoBanner: React.FCC<IProps> = () => {
  const analytics = useAnalytics()

  const handleAninixComClick = React.useCallback(() => {
    analytics.track({
      eventName: AnalyticsEvent.LottiePreviewAninixComRedirect,
      properties: {
        source: 'desktop-logo',
      },
    })
  }, [analytics])

  const handlePluginClick = React.useCallback(
    (source: string) => {
      analytics.track({
        eventName: AnalyticsEvent.LottiePreviewPluginRedirect,
        properties: {
          source,
        },
      })
    },
    [analytics]
  )

  return (
    <>
      {/* Desktop Banner - Left Side */}
      <div className="fixed left-0 top-0 z-50 hidden h-screen w-[340px] flex-col justify-between bg-secondary p-8 text-white lg:flex">
        {/* Logo section */}
        <a
          href="https://aninix.com/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleAninixComClick}
          className="flex flex-row items-center gap-3 transition-opacity duration-200 hover:opacity-80"
        >
          <AninixIcon />
          <p className="text-lg font-bold leading-tight">Aninix</p>
        </a>

        {/* Main content */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <h2 className="font-header text-3xl font-bold leading-tight">
              Want to animate your designs?
            </h2>
            <p className="text-base leading-relaxed text-gray-300">
              If you have a Figma design or SVG and want to animate it in the
              browser, export to Lottie, MP4, GIF, WebM, save and manage your
              animations — try Aninix plugin.
            </p>
          </div>

          {/* Features list */}
          <div className="flex flex-col gap-3">
            <FeatureItem
              icon={<DesignIcon />}
              text="Import from Figma or SVG"
            />
            <FeatureItem icon={<AnimateIcon />} text="Animate in the browser" />
            <FeatureItem
              icon={<ExportIcon />}
              text="Export to Lottie, MP4, GIF, WebM"
            />
            <FeatureItem
              icon={<SaveIcon />}
              text="Save and manage animations"
            />
          </div>

          {/* CTA Button */}
          <a
            href="https://www.figma.com/community/plugin/988173868842375596"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handlePluginClick('desktop-cta-button')}
            className="group flex w-full flex-row items-center justify-between rounded-lg bg-accent px-6 py-4 text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
          >
            <div className="flex flex-row items-center gap-3">
              <FigmaIcon />
              <span className="text-base font-semibold">Try Aninix plugin</span>
            </div>
            <ArrowIcon />
          </a>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2">
          <div className="h-px w-full bg-gray-500 opacity-30" />
          <p className="text-xs text-gray-400">
            © 2021-{new Date().getFullYear()} Aninix Inc. All rights reserved
          </p>
        </div>
      </div>

      {/* Mobile Banner - Bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex w-full items-center justify-between gap-4 border-t border-gray-500 border-opacity-20 bg-secondary px-4 py-4 text-white lg:hidden">
        <div className="flex flex-row items-center gap-3">
          r
          <a
            href="https://aninix.com/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analytics.track({
                eventName: AnalyticsEvent.LottiePreviewAninixComRedirect,
                properties: {
                  source: 'mobile-logo',
                },
              })
            }}
            className="flex-shrink-0 transition-opacity duration-200 hover:opacity-80"
          >
            <AninixIcon />
          </a>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold leading-tight">
              Animate in Figma
            </p>
            <p className="text-xs text-gray-300">
              Export to Lottie, MP4, GIF, WebM
            </p>
          </div>
        </div>
        <a
          href="https://www.figma.com/community/plugin/988173868842375596"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handlePluginClick('mobile-cta-button')}
          className="flex flex-shrink-0 flex-row items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02]"
        >
          <FigmaIcon />
          <span>Try plugin</span>
        </a>
      </div>
    </>
  )
}

PromoBanner.displayName = 'PromoBanner'

// Feature item component
interface FeatureItemProps {
  icon: React.ReactNode
  text: string
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, text }) => (
  <div className="flex flex-row items-center gap-3">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent bg-opacity-20">
      {icon}
    </div>
    <p className="text-sm text-gray-200">{text}</p>
  </div>
)

// Icons
const AninixIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="48" height="48" rx="12" fill="#374FD5" />
    <path
      d="M31.9688 23.5781L20.8594 33.4688V15.1875L16.9219 17.2504V37.0781L19.4062 39.0938L37.6875 22.6875L19.125 7.96875L15.1875 10.0312L31.9688 23.5781Z"
      fill="white"
    />
  </svg>
)

const DesignIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13 2H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V3C14 2.44772 13.5523 2 13 2Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 6H11M5 10H9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const AnimateIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 8L6.5 5.5V10.5L10.5 8Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ExportIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SaveIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 14H4C3.46957 14 2.96086 13.7893 2.58579 13.4142C2.21071 13.0391 2 12.5304 2 12V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H10L14 6V12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11 14V9H5V14M5 2V6H9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const UploadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.3333 5.33333L8 2L4.66667 5.33333M8 2V10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ArrowIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="transition-transform duration-200 group-hover:translate-x-1"
  >
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FigmaIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.66667 20C8.50762 20 10 18.5076 10 16.6667V13.3333H6.66667C4.82572 13.3333 3.33333 14.8257 3.33333 16.6667C3.33333 18.5076 4.82572 20 6.66667 20Z"
      fill="#0ACF83"
    />
    <path
      d="M3.33333 10C3.33333 8.15905 4.82572 6.66667 6.66667 6.66667H10V13.3333H6.66667C4.82572 13.3333 3.33333 11.8409 3.33333 10Z"
      fill="#A259FF"
    />
    <path
      d="M3.33333 3.33333C3.33333 1.49238 4.82572 0 6.66667 0H10V6.66667H6.66667C4.82572 6.66667 3.33333 5.17429 3.33333 3.33333Z"
      fill="#F24E1E"
    />
    <path
      d="M10 0H13.3333C15.1743 0 16.6667 1.49238 16.6667 3.33333C16.6667 5.17429 15.1743 6.66667 13.3333 6.66667H10V0Z"
      fill="#FF7262"
    />
    <path
      d="M16.6667 10C16.6667 11.8409 15.1743 13.3333 13.3333 13.3333C11.4924 13.3333 10 11.8409 10 10C10 8.15905 11.4924 6.66667 13.3333 6.66667C15.1743 6.66667 16.6667 8.15905 16.6667 10Z"
      fill="#1ABCFE"
    />
  </svg>
)
